// 051 — Bloques de identificación (Fase 4, Tanda 1a, ver PLAN.md). Un solo
// componente — `Formulario051.tsx` (Tanda 1a) lo inserta completo, antes de
// los bloques de Parto/Postparto/Morbilidad/Intervenciones (Tanda 1b).
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BloqueGestacionActual } from './Bloques051Gestacion';
import { BloqueControlesPrenatales } from './Bloques051Controles';
import { CampoDictado } from './CampoDictado';
import { SiNoBotones } from './camposSimples';
import { leerSiNo, type SiNoValor } from './camposSimplesDatos';
import {
  ANTECEDENTES_FAMILIARES_OPCIONES,
  ANTECEDENTES_PERSONALES_OPCIONES,
  CONTADORES_OBSTETRICOS,
  DIABETES_TIPO_OPCIONES,
  FIN_EMBARAZO_ANTERIOR_OPCIONES,
  hayAbortos051,
  AUTOIDENTIFICACION_OPCIONES,
  ESTADO_CIVIL_051_OPCIONES,
  FRACASO_METODO_ANTICONCEPTIVO_OPCIONES,
  NACIONALIDAD_MATERNA_OPCIONES,
  NIVEL_EDUCACION_051_OPCIONES,
  ULTIMO_EMBARAZO_OPCIONES,
  type Autoidentificacion,
  type EstadoAntecedentesFamiliares051,
  type EstadoAntecedentesObstetricos051,
  type EstadoAntecedentesPersonales051,
  type EstadoCivil051,
  type EstadoDatosGenerales051,
  type DiabetesTipo,
  type EstadoIdentificacion051,
  type FinEmbarazoAnterior,
  type FracasoMetodoAnticonceptivo,
  type NacionalidadMaterna,
  type NivelEducacion051,
  type UltimoEmbarazo,
} from './bloques051IdentificacionDatos';

function CampoTexto({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return <CampoDictado id={id} etiqueta={label} valor={value} onChange={onChange} />;
}

function CampoFecha({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="date" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function CampoSelect<T extends string>({
  id,
  label,
  value,
  opciones,
  etiquetaOpcion,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: T | '';
  readonly opciones: readonly T[];
  readonly etiquetaOpcion: (op: T) => string;
  readonly onChange: (v: T) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value || undefined} onValueChange={(v) => onChange(v as T)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {opciones.map((op) => (
            <SelectItem key={op} value={op}>
              {etiquetaOpcion(op)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CampoCheckbox({ id, label, checked, disabled, onChange }: { readonly id: string; readonly label: string; readonly checked: boolean; readonly disabled?: boolean; readonly onChange: (v: boolean) => void }): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} disabled={disabled} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
    </div>
  );
}

function BloqueDatosGenerales({ estado, onChange }: { readonly estado: EstadoDatosGenerales051; readonly onChange: (v: EstadoDatosGenerales051) => void }): JSX.Element {
  const { t } = useTranslation();
  function actualizar(cambios: Partial<EstadoDatosGenerales051>): void {
    onChange({ ...estado, ...cambios });
  }
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f051.datosGenerales.titulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <CampoTexto id="f051-telefono" label={t('formularios.f051.datosGenerales.telefono')} value={estado.telefono} onChange={(v) => actualizar({ telefono: v })} />
        <CampoSelect<Autoidentificacion>
          id="f051-autoidentificacion"
          label={t('formularios.f051.datosGenerales.autoidentificacion')}
          value={estado.autoidentificacion}
          opciones={AUTOIDENTIFICACION_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.datosGenerales.autoidentificacionOpciones.${op}`)}
          onChange={(v) => actualizar({ autoidentificacion: v })}
        />
        <CampoSelect<NacionalidadMaterna>
          id="f051-nacionalidad"
          label={t('formularios.f051.datosGenerales.nacionalidad')}
          value={estado.nacionalidad}
          opciones={NACIONALIDAD_MATERNA_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.datosGenerales.nacionalidadOpciones.${op}`)}
          onChange={(v) => actualizar({ nacionalidad: v })}
        />
        <CampoTexto id="f051-nacionalidad-cual" label={t('formularios.f051.datosGenerales.nacionalidadCual')} value={estado.nacionalidadCual} onChange={(v) => actualizar({ nacionalidadCual: v })} />
        <CampoTexto id="f051-provincia" label={t('formularios.f051.datosGenerales.provincia')} value={estado.provincia} onChange={(v) => actualizar({ provincia: v })} />
        <CampoTexto id="f051-canton" label={t('formularios.f051.datosGenerales.canton')} value={estado.canton} onChange={(v) => actualizar({ canton: v })} />
        <CampoTexto id="f051-parroquia" label={t('formularios.f051.datosGenerales.parroquia')} value={estado.parroquia} onChange={(v) => actualizar({ parroquia: v })} />
        <CampoTexto id="f051-domicilio" label={t('formularios.f051.datosGenerales.domicilio')} value={estado.domicilio} onChange={(v) => actualizar({ domicilio: v })} />
        <CampoSelect<EstadoCivil051>
          id="f051-estado-civil"
          label={t('formularios.f051.datosGenerales.estadoCivil')}
          value={estado.estadoCivil}
          opciones={ESTADO_CIVIL_051_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.datosGenerales.estadoCivilOpciones.${op}`)}
          onChange={(v) => actualizar({ estadoCivil: v })}
        />
        <CampoSelect<NivelEducacion051>
          id="f051-nivel-educacion"
          label={t('formularios.f051.datosGenerales.nivelEducacion')}
          value={estado.nivelEducacion}
          opciones={NIVEL_EDUCACION_051_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.datosGenerales.nivelEducacionOpciones.${op}`)}
          onChange={(v) => actualizar({ nivelEducacion: v })}
        />
        <CampoTexto id="f051-establecimiento-lugar-parto" label={t('formularios.f051.datosGenerales.establecimientoLugarParto')} value={estado.establecimientoLugarParto} onChange={(v) => actualizar({ establecimientoLugarParto: v })} />
      </div>
      <CampoCheckbox id="f051-sabe-escribir" label={t('formularios.f051.datosGenerales.sabeEscribir')} checked={estado.sabeEscribir} onChange={(v) => actualizar({ sabeEscribir: v })} />
    </div>
  );
}

/** Una fila Sí / No por clave (el papel trae los círculos "no" y "si" por ítem). */
function GrillaSiNo({
  titulo,
  claves,
  prefijoI18n,
  prefijoId,
  valores,
  onChange,
}: {
  readonly titulo: string;
  readonly claves: readonly string[];
  readonly prefijoI18n: string;
  readonly prefijoId: string;
  readonly valores: Readonly<Record<string, SiNoValor>>;
  readonly onChange: (v: Readonly<Record<string, SiNoValor>>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <fieldset className="grid min-w-0 grid-cols-1 gap-3 border-0 p-0 sm:grid-cols-2 md:grid-cols-3">
      <legend className="sr-only">{titulo}</legend>
      {claves.map((c) => (
        <SiNoBotones key={c} id={`${prefijoId}-${c}`} etiqueta={t(`${prefijoI18n}.${c}`)} valor={leerSiNo(valores[c])} textos={{ si: t('formulario.si'), no: t('formulario.no') }} onChange={(v) => onChange({ ...valores, [c]: v })} />
      ))}
    </fieldset>
  );
}

function BloqueAntecedentesFamiliaresPersonales({
  familiares,
  personales,
  onChangeFamiliares,
  onChangePersonales,
}: {
  readonly familiares: EstadoAntecedentesFamiliares051;
  readonly personales: EstadoAntecedentesPersonales051;
  readonly onChangeFamiliares: (v: EstadoAntecedentesFamiliares051) => void;
  readonly onChangePersonales: (v: EstadoAntecedentesPersonales051) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const p = 'formularios.f051.antecedentesPersonales';
  const otraSi = leerSiNo(familiares.presentes.otraCondicionGrave) === 'si' || leerSiNo(personales.presentes.otraCondicionGrave) === 'si';
  const diabetesSi = leerSiNo(personales.presentes.diabetes) === 'si';
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f051.antecedentesFamiliares.titulo')}</h4>
      <GrillaSiNo
        titulo={t('formularios.f051.antecedentesFamiliares.titulo')}
        claves={ANTECEDENTES_FAMILIARES_OPCIONES}
        prefijoI18n="formularios.f051.antecedentesFamiliares.opciones"
        prefijoId="f051-af"
        valores={familiares.presentes}
        onChange={(v) => onChangeFamiliares({ ...familiares, presentes: v })}
      />
      <h4 className="text-sm font-medium text-hce-ink">{t(`${p}.titulo`)}</h4>
      <GrillaSiNo titulo={t(`${p}.titulo`)} claves={ANTECEDENTES_PERSONALES_OPCIONES} prefijoI18n={`${p}.opciones`} prefijoId="f051-ap" valores={personales.presentes} onChange={(v) => onChangePersonales({ ...personales, presentes: v })} />
      {diabetesSi && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
          <CampoSelect<DiabetesTipo>
            id="f051-ap-diabetes-tipo"
            label={t(`${p}.diabetesTipo`)}
            value={personales.diabetesTipo}
            opciones={DIABETES_TIPO_OPCIONES}
            etiquetaOpcion={(op) => t(`${p}.diabetesTipoOpciones.${op}`)}
            onChange={(v) => onChangePersonales({ ...personales, diabetesTipo: v })}
          />
        </div>
      )}
      {otraSi && <CampoTexto id="f051-ap-otra-cual" label={t(`${p}.otraCual`)} value={personales.otraCual} onChange={(v) => onChangePersonales({ ...personales, otraCual: v })} />}
    </div>
  );
}

function BloqueAntecedentesObstetricos({ estado, onChange }: { readonly estado: EstadoAntecedentesObstetricos051; readonly onChange: (v: EstadoAntecedentesObstetricos051) => void }): JSX.Element {
  const { t } = useTranslation();
  const p = 'formularios.f051.antecedentesObstetricos';
  function actualizar(cambios: Partial<EstadoAntecedentesObstetricos051>): void {
    onChange({ ...estado, ...cambios });
  }
  const hayAbortos = hayAbortos051(estado);
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t(`${p}.titulo`)}</h4>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {CONTADORES_OBSTETRICOS.map((c) => (
          <CampoTexto key={c} id={`f051-ao-${c}`} label={t(`${p}.${c}`)} value={estado[c]} onChange={(v) => actualizar({ [c]: v })} />
        ))}
      </div>
      <CampoCheckbox
        id="f051-ao-tres-espontaneos"
        label={t(`${p}.tresEspontaneosConsecutivos`)}
        checked={estado.tresEspontaneosConsecutivos && hayAbortos}
        disabled={!hayAbortos}
        onChange={(v) => actualizar({ tresEspontaneosConsecutivos: v })}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <CampoFecha id="f051-fum" label={t(`${p}.fechaUltimaMenstruacion`)} value={estado.fechaUltimaMenstruacion} onChange={(v) => actualizar({ fechaUltimaMenstruacion: v })} />
        <CampoFecha id="f051-fpp" label={t(`${p}.fechaProbableParto`)} value={estado.fechaProbableParto} onChange={(v) => actualizar({ fechaProbableParto: v })} />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <CampoSelect<UltimoEmbarazo>
          id="f051-ultimo-embarazo"
          label={t(`${p}.ultimoEmbarazo`)}
          value={estado.ultimoEmbarazo}
          opciones={ULTIMO_EMBARAZO_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.ultimoEmbarazoOpciones.${op}`)}
          onChange={(v) => actualizar({ ultimoEmbarazo: v })}
        />
        <SiNoBotones id="f051-ao-embarazos-multiples" etiqueta={t(`${p}.embarazosMultiples`)} valor={estado.embarazosMultiples} textos={{ si: t('formulario.si'), no: t('formulario.no') }} onChange={(v) => actualizar({ embarazosMultiples: v })} />
        <SiNoBotones id="f051-ao-embarazo-planificado" etiqueta={t(`${p}.embarazoPlanificado`)} valor={estado.embarazoPlanificado} textos={{ si: t('formulario.si'), no: t('formulario.no') }} onChange={(v) => actualizar({ embarazoPlanificado: v })} />
        <CampoSelect<FinEmbarazoAnterior>
          id="f051-fin-embarazo-anterior"
          label={t(`${p}.finEmbarazoAnterior`)}
          value={estado.finEmbarazoAnterior}
          opciones={FIN_EMBARAZO_ANTERIOR_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.finEmbarazoAnteriorOpciones.${op}`)}
          onChange={(v) => actualizar({ finEmbarazoAnterior: v })}
        />
        {estado.finEmbarazoAnterior !== 'noAplica' && (
          <CampoFecha id="f051-fin-embarazo-fecha" label={t(`${p}.finEmbarazoAnteriorFecha`)} value={estado.finEmbarazoAnteriorFecha} onChange={(v) => actualizar({ finEmbarazoAnteriorFecha: v })} />
        )}
        <CampoSelect<FracasoMetodoAnticonceptivo>
          id="f051-fracaso-metodo"
          label={t(`${p}.fracasoMetodoAnticonceptivo`)}
          value={estado.fracasoMetodoAnticonceptivo}
          opciones={FRACASO_METODO_ANTICONCEPTIVO_OPCIONES}
          etiquetaOpcion={(op) => t(`${p}.fracasoMetodoAnticonceptivoOpciones.${op}`)}
          onChange={(v) => actualizar({ fracasoMetodoAnticonceptivo: v })}
        />
      </div>
    </div>
  );
}

export interface Bloques051IdentificacionProps {
  readonly estado: EstadoIdentificacion051;
  readonly onChange: (estado: EstadoIdentificacion051) => void;
}

export function Bloques051Identificacion({ estado, onChange }: Bloques051IdentificacionProps): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <BloqueDatosGenerales estado={estado.datosGenerales} onChange={(v) => onChange({ ...estado, datosGenerales: v })} />
      <BloqueAntecedentesFamiliaresPersonales
        familiares={estado.antecedentesFamiliares}
        personales={estado.antecedentesPersonales}
        onChangeFamiliares={(v) => onChange({ ...estado, antecedentesFamiliares: v })}
        onChangePersonales={(v) => onChange({ ...estado, antecedentesPersonales: v })}
      />
      <BloqueAntecedentesObstetricos estado={estado.antecedentesObstetricos} onChange={(v) => onChange({ ...estado, antecedentesObstetricos: v })} />
      <BloqueGestacionActual estado={estado.gestacionActual} onChange={(v) => onChange({ ...estado, gestacionActual: v })} />
      <BloqueControlesPrenatales estado={estado.controlPrenatal} onChange={(v) => onChange({ ...estado, controlPrenatal: v })} />
    </div>
  );
}
